import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../../environments/environment';

// Importaciones de servicios.
import { ActivationService } from '../../../services/activation.service';
import { DansapService } from '../../../services/dansap.service';
import { LogService } from '../../../services/log.service';

// Importaciones de interfaces.
import { IActivation } from '../../../interfaces/activation.interface';
import { IActivationResponse } from '../../../interfaces/activation-response.interface';

// Importaciones de modelos.
import { Activation } from '../../../models/activation.model';
import { Customer } from '../../../models/customer.model';
import { Employee } from '../../../models/employee.model';
import { Host } from '../../../models/host.model';
import { License } from '../../../models/license.model';
import { Log } from '../../../models/log.model';
import { Product } from '../../../models/product.model';

/**
 * Componente de configuración de activaciones.
 *
 * Este componente permite gestionar y configurar las activaciones en la aplicación.
 * Proporciona una interfaz para visualizar, seleccionar y enviar activaciones a otros servicios
 * como Dansap. Además, maneja la carga y presentación de datos en una tabla.
 */
@Component({
  selector: 'app-activation-settings',
  templateUrl: './activation-settings.component.html',
  standalone: false,
})
export class ActivationSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si los filtros deben ser mostrados en la interfaz de usuario.
   * Es un valor booleano que controla la visibilidad de la sección de filtros.
   */
  showFilters: boolean = false;

  /**
   * Estado que indica si las activaciones están siendo cargadas.
   * Se usa para mostrar un indicador de carga en la interfaz.
   */
  loadingActivations: boolean = false;

  /**
   * Estado que indica si el proceso de envío a Dansap está en curso.
   * Se usa para deshabilitar acciones mientras se completa el envío.
   */
  loadingSendDansap: boolean = false;

  /**
   * Lista de activaciones que contiene los objetos de tipo `IActivation`.
   * Se utiliza para almacenar y manipular las activaciones en la aplicación.
   */
  activations: IActivation[] = [];

  /**
   * Objeto de tipo `FormGroup` que agrupa los controles del formulario de filtros.
   * Se utiliza para gestionar y validar los campos de filtro en la interfaz.
   */
  form: FormGroup = new FormGroup({
    serial: new FormControl(),
    productName: new FormControl(),
    licenseName: new FormControl(),
    hostName: new FormControl(),
    customerName: new FormControl(),
    employeeDisplayName: new FormControl(),
    startDate: new FormControl(),
    endDate: new FormControl(),
  });

  /**
   * Columnas que se mostrarán en la tabla de activaciones.
   * Contiene los identificadores de las columnas visibles en la tabla.
   */
  tableColumns: string[] = [
    'selected',
    'serial',
    'product.name',
    'license.name',
    'host.name',
    'customer.name',
    'comment',
    'employee.displayName',
    'sent',
    'createdAt',
  ];

  /**
   * Fuente de datos para la tabla de activaciones.
   * Utiliza `MatTableDataSource` para gestionar y mostrar las activaciones en la tabla.
   */
  tableData: MatTableDataSource<IActivation> =
    new MatTableDataSource<IActivation>();

  /**
   * Referencia al paginador de la tabla de activaciones.
   *
   * Utiliza el decorador `@ViewChild` para acceder a la instancia del paginador
   * asociado a la tabla de activaciones. Esto permite controlar la paginación
   * de los datos de la tabla a través de la instancia de `MatPaginator`.
   *
   * @see MatPaginator - Componente de Angular Material para la paginación de tablas.
   */
  @ViewChild(MatPaginator) tablePaginator!: MatPaginator;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de activaciones.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort!: MatSort;

  /**
   * Obtiene el control del formulario para el campo 'serial'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formSerial(): AbstractControl | null {
    return this.form.get('serial');
  }

  /**
   * Obtiene el control del formulario para el campo 'productName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formProductName(): AbstractControl | null {
    return this.form.get('productName');
  }

  /**
   * Obtiene el control del formulario para el campo 'licenseName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formLicenseName(): AbstractControl | null {
    return this.form.get('licenseName');
  }

  /**
   * Obtiene el control del formulario para el campo 'hostName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formHostName(): AbstractControl | null {
    return this.form.get('hostName');
  }

  /**
   * Obtiene el control del formulario para el campo 'customerName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formCustomerName(): AbstractControl | null {
    return this.form.get('customerName');
  }

  /**
   * Obtiene el control del formulario para el campo 'employeeDisplayName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formEmployeeDisplayName(): AbstractControl | null {
    return this.form.get('employeeDisplayName');
  }

  /**
   * Obtiene el control del formulario para el campo 'startDate'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formStartDate(): AbstractControl | null {
    return this.form.get('startDate');
  }

  /**
   * Obtiene el control del formulario para el campo 'endDate'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formEndDate(): AbstractControl | null {
    return this.form.get('endDate');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el correcto funcionamiento
   * del componente o servicio. En este caso, se inyectan `ActivationService`
   * y `DansapService` para gestionar las activaciones y la interacción con Dansap.
   *
   * @param activationService - Servicio encargado de gestionar las activaciones.
   * @param dansapService - Servicio encargado de interactuar con Dansap.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio de Angular Material utilizado para mostrar mensajes emergentes en la interfaz de usuario.
   */
  constructor(
    private activationService: ActivationService,
    private dansapService: DansapService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Recarga los datos de la tabla.
   *
   * Este método reinicia la configuración o los datos de la tabla
   * llamando al método `initTable`. Se utiliza para actualizar
   * la información mostrada en la tabla, asegurándose de que los
   * datos estén actualizados y reflejen los cambios recientes.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Envía las activaciones seleccionadas al Dansap de forma asíncrona.
   *
   * Esta función procesa las activaciones seleccionadas y las envía
   * al servicio Dansap. Maneja posibles errores y muestra un mensaje
   * al usuario si el envío falla.
   */
  async sendDansap(): Promise<void> {
    this.loadingSendDansap = true;
    let errorsOnSendDansapActivations = false;

    const selectedActivations: IActivation[] = this.tableData.data.filter(
      (activation) => activation.selected
    );

    const promises = selectedActivations.map((activation) => {
      return new Promise(async (resolve, reject) => {
        try {
          await this.dansapService.sendActivation(activation);
          activation.sent = 1;
          await this.activationService.update(activation);

          resolve(`Activación ${activation.id} enviada con éxito.`);
        } catch (errors: any) {
          errorsOnSendDansapActivations = true;
          reject(errors);
        }
      });
    });

    await Promise.allSettled(promises);

    if (!errorsOnSendDansapActivations) {
      // Añadir un registro de envío de activaciones al Dansap.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha enviado activaciones al Dansap.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de envío de activaciones al Dansap.'
          );
        }
      }

      this.snackBar.open(
        'Ha enviado las activaciones al Dansap con éxito.',
        '',
        { duration: 5000 }
      );
    }

    this.loadingSendDansap = false;
  }

  /**
   * Filtra las activaciones según los criterios proporcionados en los formularios.
   *
   * Los filtros incluyen serial, nombre de la licencia, host, cliente, empleado y fechas de inicio y fin.
   * La búsqueda es insensible a mayúsculas/minúsculas y las fechas se comparan con la fecha de creación.
   * Los filtros no se aplican si el campo correspondiente está vacío.
   */
  filter(): void {
    const serial = this.formSerial?.value?.toLowerCase().trim();
    const productName = this.formProductName?.value?.toLowerCase().trim();
    const licenseName = this.formLicenseName?.value?.toLowerCase().trim();
    const hostName = this.formHostName?.value?.toLowerCase().trim();
    const customerName = this.formCustomerName?.value?.toLowerCase().trim();
    const employeeDisplayName = this.formEmployeeDisplayName?.value
      ?.toLowerCase()
      .trim();
    const startDate = this.formStartDate?.value
      ? new Date(this.formStartDate.value)
      : undefined;
    const endDate = this.formEndDate?.value
      ? new Date(this.formEndDate.value)
      : undefined;

    this.tableData.data = this.activations.filter((activation: IActivation) => {
      const activationCreatedAt = activation.createdAt
        ? new Date(activation.createdAt)
        : undefined;

      return (
        (!serial || activation.serial?.toLowerCase().includes(serial)) &&
        (!productName ||
          activation.license.product.name
            ?.toLowerCase()
            .includes(productName)) &&
        (!licenseName ||
          activation.license.name?.toLowerCase().includes(licenseName)) &&
        (!hostName || activation.host.name?.toLowerCase().includes(hostName)) &&
        (!customerName ||
          activation.customer.name?.toLowerCase().includes(customerName)) &&
        (!employeeDisplayName ||
          activation.employee.displayName
            ?.toLowerCase()
            .includes(employeeDisplayName)) &&
        (!startDate ||
          (activationCreatedAt && activationCreatedAt >= startDate)) &&
        (!endDate || (activationCreatedAt && activationCreatedAt <= endDate))
      );
    });
  }

  /**
   * Activa o desactiva todos los checkboxes de la página actual.
   *
   * @param checked - `true` para marcar todos los checkboxes, `false` para desmarcarlos.
   */
  toggleCheckboxes(checked: boolean): void {
    if (checked) {
      const pageSize = this.tableData.paginator?.pageSize ?? 0;
      const pageIndex = this.tableData.paginator?.pageIndex ?? 0;

      const start = pageSize * pageIndex;
      const end = start + pageSize;

      this.tableData.data
        .slice(start, end)
        .forEach((activation) => (activation.selected = checked));
    } else {
      this.tableData.data.forEach(
        (activation) => (activation.selected = checked)
      );
    }
  }

  /**
   * Inicializa y carga los datos de la tabla de forma asíncrona.
   *
   * Este método obtiene los datos necesarios para la tabla, asegurándose
   * de que se carguen correctamente antes de que puedan ser utilizados.
   * También puede incluir lógica para restablecer filtros, paginación u otros estados.
   */
  private async initTable(): Promise<void> {
    this.loadingActivations = true;
    this.tableData = new MatTableDataSource<IActivation>();

    try {
      // Obtener las activaciones y añadir-las en la tabla.
      const activationResponse: IActivationResponse =
        await this.activationService.getAll();

      this.activations = activationResponse.activations.map(
        (activation: IActivation) => {
          return new Activation({
            ...activation,
            license: new License({
              ...activation.license,
              product: new Product(activation.license.product),
            }),
            host: new Host(activation.host),
            customer: new Customer(activation.customer),
            employee: new Employee(activation.employee),
          });
        }
      );

      this.tableData = new MatTableDataSource<IActivation>(this.activations);
    } finally {
      this.configureTable();
      this.loadingActivations = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }
}
